import { Button, Card, FormattedMessage, Icon, Tooltip, useIntl } from "@metsooutotec/modes-react-components"
import ListView from "components/utils/list-view/list-view"
import { FC, useState, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import customerStore from "features/organizations/customers/store/customer-store"
import { NavigationModalOptions } from "components/utils/modal-helper"
import { Asset } from "features/organizations/assets/models/asset-models"
import AssetModal from "features/organizations/assets/components/asset-modal"
import styles from "./assets-card.module.css"

const AssetsCard: FC = () => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { siteId, customerId } = useParams()
    const [assetToEdit, setAssetToEdit] = useState<Asset>()
    const [isAssetModalOpen, setAssetModalOpen] = useState(false)

    const customer = customerId ? customerStore.customerMap[customerId] : undefined
    const site = customer?.sites?.find(s => s.id === siteId)

    const navStub = `/customers/${customerId}/sites/${siteId}/assets`

    const handleEditAssetIconClick = (event: React.MouseEvent, asset: Asset) => {
        event.stopPropagation()
        setAssetToEdit(asset)
        setAssetModalOpen(true)
    }

    const createAssetModal = {
        open: () => setAssetModalOpen(true),
        close: (passed: boolean, assetId?: string, options?: NavigationModalOptions) => {
            setAssetModalOpen(false)
            setAssetToEdit(undefined)

            if (passed && assetId) {
                navigate(`${navStub}/${assetId}`, { state: options })
            }
        },
    }

    const sortedAssets = useMemo(() => site?.assets?.slice().sort((a, b) => a.name.localeCompare(b.name)) || [], [site?.assets])

    const template = useMemo(
        () => [
            {
                headingKey: "headings.asset",
                cellFunc: (a: Asset) => a.name,
            },
            {
                headingKey: "headings.assetDescription",
                cellFunc: (a: Asset) => a.description,
            },
            {
                headingKey: "asset.wearThreshold",
                cellFunc: (a: Asset) => a.wearThreshold,
            },
            {
                headingKey: "headings.icon",
                cellFunc: (a: Asset) => <Icon name='icon-edit-16' onClick={(e: React.MouseEvent) => handleEditAssetIconClick(e, a)} />,
            },
        ],
        [],
    )
    if (!site) return null
    return (
        <>
            <Card
                content={
                    <div className={styles.assetsCardContents}>
                        <ListView
                            data={sortedAssets}
                            noneYetKey='assetList.noAssetsYet'
                            onClickRow={(a: Asset) => navigate(`${navStub}/${a.id}`)}
                            template={template}
                        />
                    </div>
                }
                header={<FormattedMessage id='assetList.title' />}
                rightHeaderItem={
                    <Tooltip
                        content={formatMessage("asset.addNew")}
                        data-cy='tooltip'
                        position='top center'
                        small
                        trigger={<Button icon='icon-plus-16' onClick={createAssetModal.open} subtle primary />}
                    />
                }
            />
            <AssetModal isOpen={isAssetModalOpen} asset={assetToEdit} onClose={createAssetModal.close} siteId={siteId} customerId={customerId} />
        </>
    )
}

export default AssetsCard
